// new Date() con un valor inservible no lanza: devuelve un Date invalido, que no
// es null, se cuela por cualquier guard de null y llega a pantalla como
// "Invalid date". El caso real fue el backend mandando el STRING "None" cuando
// el comprobante aun no estaba autorizado. Eso ya se arreglo en el serializer,
// pero el modelo no deberia depender de que el emisor se porte bien.
const parseDate = (value) => {
    if (value === undefined || value === null || value === '' || value === 'None') { return null }
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
};

const EDocument = class {
    constructor(object) {
        if (object === undefined) {
            this.id = null;
            this.serial = null;
            this.sequence = null;
            this.accessCode = null;
            this.received = null;
            this.authorized = null;
            this.nulled = null;
            this.createdAt = null;
            this.sentAt = null;
            this.authorizedAt = null;
            this.observations = null;
            this.status = null;
            this.annulmentStatus = 'none';
            this.annulment = null;
            this.annulmentWindow = null;
        } else {
            this.id = object.id;
            this.serial = object.serial;
            this.sequence = object.sequence;
            this.accessCode = object.accessCode;
            this.received = object.received;
            this.authorized = object.authorized;
            this.nulled = object.nulled;
            this.createdAt = parseDate(object.createdAt);
            this.observations = object.observations || null;
            this.status = object.status;

            // Estado de la solicitud de anulacion ante el SRI. OJO: mientras esta
            // en curso el comprobante SIGUE SIENDO VALIDO y `nulled` es false; en
            // retenciones y notas de credito el receptor tiene 5 dias habiles para
            // aceptarla, y si no responde el comprobante queda vigente.
            this.annulmentStatus = object.annulmentStatus || 'none';
            this.annulment = object.annulment || null;

            // Plazo del SRI para anular (dia 7 del mes siguiente a la emision).
            // Solo viene en los autorizados; el backend decide si ya vencio y
            // manda el texto, el mismo que usan sus guardas.
            this.annulmentWindow = object.annulmentWindow || null;

            this.sentAt = parseDate(object.sentAt);
            this.authorizedAt = parseDate(object.authorizedAt);
        }
    }

    // La solicitud esta viajando al portal del SRI. Dura segundos.
    get annulmentSending() { return this.annulmentStatus === 'sending' }

    // La solicitud entro y espera que el receptor la acepte o rechace.
    get annulmentPending() { return this.annulmentStatus === 'requested' }

    // Cualquiera de las dos: no se puede volver a pedir la anulacion, y tampoco
    // se revirtio nada todavia.
    get annulmentInFlight() { return this.annulmentSending || this.annulmentPending }

    // Anulado aca pero vigente para el SRI: divergencia que hay que regularizar.
    // Solo si el SRI lo autorizo: un 'unreported' sin autorizar (asi lo dejaba
    // "Anular sin reportar" sobre uno pendiente) no tiene nada que reportar, y el
    // barrido del backend lo limpia.
    get annulmentUnreported() { return this.annulmentStatus === 'unreported' && this.authorized === true }

    // Paso el plazo del SRI para anular: ni el portal ni el usuario a mano pueden
    // ya, y anular solo en Contablus no tendria vuelta. Los formularios frenan
    // ANTES de confirmar; el backend rechaza igual.
    get annulmentExpired() { return !!(this.annulmentWindow && this.annulmentWindow.expired) }

    get annulmentExpiredMessage() {
        return this.annulmentExpired ? this.annulmentWindow.message : null;
    }

    // Ultimo dia en que el receptor puede responder. Solo tiene sentido con la
    // solicitud ya registrada.
    get annulmentDeadline() {
        if (!this.annulment || !this.annulment.deadline) { return null }
        const parts = String(this.annulment.deadline).split('-');
        if (parts.length !== 3) { return null }
        return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    }

    get annulmentDeadlineFormatted() {
        const date = this.annulmentDeadline;
        if (date === null) { return null }
        return date.toLocaleDateString('es-EC', {year: 'numeric', month: '2-digit', day: '2-digit'});
    }

    // Una sola linea para los TOASTS (el panel usa annulmentNotice, que separa
    // titulo y detalle). Lo importante que tiene que transmitir es que el
    // comprobante TODAVIA es valido: no alcanza con decir "anulacion en curso".
    get annulmentMessage() {
        if (this.annulmentSending) {
            return 'Enviando la solicitud de anulación al SRI…';
        }
        if (this.annulmentPending) {
            const deadline = this.annulmentDeadlineFormatted;
            const plazo = deadline ? ` Tiene hasta el ${deadline}.` : '';
            return 'El SRI recibió la solicitud y espera que el receptor la acepte.' +
                   plazo + ' Hasta entonces el comprobante sigue siendo válido.';
        }
        if (this.annulmentUnreported) {
            return 'Este comprobante se anuló en Contablus pero NO se le informó al ' +
                   'SRI: para el SRI sigue vigente. Repórtalo cuando cargues las ' +
                   'credenciales del portal, o anúlalo a mano desde el SRI en línea.';
        }
        if (this.annulmentStatus === 'dismissed') {
            return 'La solicitud de anulación no prosperó: el receptor la rechazó o ' +
                   'venció el plazo. El comprobante sigue siendo válido.';
        }
        return null;
    }

    // AVISO UNICO del estado de la anulacion, para el panel de datos del
    // comprobante. Cubre los cinco estados: no hay un segundo cartel en otra
    // parte de la pantalla.
    //
    // Por que vive aca y no en un banner suelto: el estado de la anulacion es una
    // propiedad de ESTE comprobante, y el panel es donde vive su identidad fiscal
    // (secuencial, fechas, clave de acceso). Partirlo en dos lugares fragmenta una
    // sola idea — y ademas lo consumen cuatro pantallas, asi que una sola pieza
    // evita que los textos se separen con el tiempo.
    //
    // Reemplaza al cartel fijo "Esta factura debe ser anulada en el portal del
    // SRI", que se mostraba con solo `nulled && received` y por lo tanto mentia
    // justo cuando la anulacion SI se reporto y el SRI la acepto.
    //
    // El color codifica URGENCIA, no animo:
    //   azul     ocurriendo ahora, no hay nada que hacer
    //   amarillo informativo: esperando a un tercero, o ya cerrado sin efecto
    //   naranja  divergencia con el SRI: hay algo que hacer
    //   verde    cerrado y correcto
    get annulmentNotice() {
        if (this.annulmentSending) {
            return {
                color: 'blue',
                icon: 'pi-spin pi-spinner',
                title: 'Enviando la solicitud de anulación al SRI…',
                detail: null,
                action: null,
            };
        }

        if (this.annulmentPending) {
            const deadline = this.annulmentDeadlineFormatted;
            return {
                color: 'yellow',
                icon: 'pi-clock',
                title: 'Solicitud enviada. Falta que el receptor la acepte.',
                detail: (deadline ? `Tiene hasta el ${deadline}. ` : '') +
                        'Hasta entonces el comprobante sigue vigente.',
                action: {name: 'check', icon: 'pi pi-refresh',
                         label: 'Consultar estado en el SRI'},
            };
        }

        if (this.annulmentStatus === 'accepted') {
            return {
                color: 'green',
                icon: 'pi-check-circle',
                title: 'Anulado ante el SRI.',
                detail: 'El comprobante ya no tiene validez tributaria.',
                action: null,
            };
        }

        if (this.annulmentStatus === 'dismissed') {
            return {
                color: 'yellow',
                icon: 'pi-exclamation-circle',
                title: 'La solicitud de anulación no prosperó.',
                detail: 'El receptor la rechazó o venció el plazo, así que el ' +
                        'comprobante sigue siendo válido.',
                action: null,
            };
        }

        // Anulado aca pero vigente para el SRI. 'unreported' es el caso explicito;
        // `none` con nulled+authorized son los comprobantes anulados ANTES de que
        // existieran los estados: estan en la misma situacion y necesitan el mismo
        // aviso, asi que no se puede simplificar a mirar solo annulmentStatus.
        // Solo el primero ofrece el boton: el backend unicamente acepta reportar
        // los 'unreported', y un boton que falla es peor que no tenerlo. Uno que el
        // SRI no autorizo no existe para el: anularlo aca es completo, sin aviso.
        const vigenteParaElSRI = this.annulmentUnreported ||
            (this.nulled === true && this.authorized === true && this.annulmentStatus === 'none');
        if (vigenteParaElSRI) {
            return {
                color: 'orange',
                icon: 'pi-exclamation-triangle',
                title: 'Anulado en Contablus, pero vigente para el SRI.',
                detail: this.annulmentUnreported
                    ? null
                    : 'Hay que anularlo a mano desde el portal del SRI.',
                action: this.annulmentUnreported
                    ? {name: 'report', icon: 'pi pi-send',
                       label: 'Reportar la anulación al SRI'}
                    : null,
            };
        }

        return null;
    }
}

export default EDocument
